// ─── El código que «Configurar período» le pone a una sección ───────────────
// Desde H7 el servidor exige que el código sea ÚNICO dentro del período
// (`section.code_taken`). Esta pantalla arma el código sola (programa + curso),
// y antes lo mandaba también al MOVER o REABRIR una sección existente: dos
// secciones del mismo curso movidas al mismo programa recibían el mismo código
// (en LABTS hay 103 combinaciones período + programa + curso con más de una
// sección), y el código importado de una sección se pisaba con uno generado.
//
// Las reglas:
//  - Una sección NUEVA recibe el código generado; si ya lo usa otra sección
//    del período (cancelada o no: el servidor las cuenta todas), se le agrega
//    «-2», «-3»…
//  - Una sección EXISTENTE conserva su código. Solo se recalcula al moverla de
//    programa si su código es exactamente el que esta pantalla generó para el
//    programa de origen (si no, es un código propio o importado, y se respeta).
//  - A una sección existente SIN código no se le manda uno generado.

export function buildSectionCode(programCode: string, courseCode: string): string {
  const pc = (programCode || '').toUpperCase();
  const cc = (courseCode || '').toUpperCase();
  if (!pc) return cc;
  // Avoid double-prefixing if courseCode already starts with programCode
  if (cc.startsWith(pc + '-') || cc.startsWith(pc)) return cc;
  return `${pc}-${cc}`;
}

const norm = (c: string | undefined | null) => (c ?? '').trim().toLowerCase();

/** El código generado, libre dentro del período. `takenInTerm` son los códigos
 *  de las OTRAS secciones del período. */
export function freeSectionCode(
  programCode: string,
  courseCode: string,
  takenInTerm: readonly (string | undefined | null)[],
): string {
  const base = buildSectionCode(programCode, courseCode);
  const taken = new Set(takenInTerm.map(norm).filter(Boolean));
  if (!taken.has(norm(base))) return base;
  for (let n = 2; ; n++) {
    const candidate = `${base}-${n}`;
    if (!taken.has(norm(candidate))) return candidate;
  }
}

/** El código a MANDAR al mover una sección existente de programa; `undefined`
 *  = no mandar nada (la sección conserva el suyo). */
export function sectionCodeAfterMove(params: {
  currentCode: string | undefined | null;
  fromProgramCode: string;
  toProgramCode: string;
  courseCode: string;
  takenInTerm: readonly (string | undefined | null)[];
}): string | undefined {
  const current = (params.currentCode ?? '').trim();
  if (!current) return undefined;
  const generated = norm(buildSectionCode(params.fromProgramCode, params.courseCode));
  // También «-2», «-3»: los que esta pantalla generó para desempatar.
  const wasGenerated =
    norm(current) === generated ||
    new RegExp(`^${generated.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}-\\d+$`).test(norm(current));
  if (!wasGenerated) return undefined;
  const next = freeSectionCode(params.toProgramCode, params.courseCode, params.takenInTerm);
  return norm(next) === norm(current) ? undefined : next;
}

/** Los códigos de las otras secciones del período (todas: el servidor también
 *  cuenta las canceladas). */
export function codesInTerm(
  sections: readonly { id: string; termId?: string; sectionCode?: string | null }[],
  termId: string,
  exceptSectionId?: string,
): string[] {
  return sections
    .filter((s) => s.termId === termId && s.id !== exceptSectionId)
    .map((s) => s.sectionCode ?? '')
    .filter((c) => c.trim() !== '');
}
